import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;
const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const errorMessage = (e: any) => e?.response?.data?.error || "Une erreur est survenue";

/** Public API (/api-client) — the client's own access, as Settings shows it (never their limits). */
export interface MyApiAccess {
    enabled: boolean;
    maxPageSize?: number;
}

export const getMyApiAccess = async (): Promise<MyApiAccess> => {
    const res = await axios.get("/api-keys/account", config());
    return res.data;
};

// ── Back-office (/bo/public-api) ──────────────────────────────────────────────

export interface OwnerGroupStats {
    baseEstablishments: number;
    groups: number;
    establishmentsInGroups: number;
    peopleInGroups: number;
    multiOwners: number;
    excludedGroups: number;
    establishmentsInExcludedGroups: number;
    groupPersons: number;
    franchiseEstablishments: number;
    franchiseOnly: number;
    largestGroup: number;
    sizeHistogram: Record<string, number>;
}

export interface BuildRun {
    startedAt: string;
    finishedAt?: string;
    error?: string;
    trigger: string;
}

export interface BuildStatus {
    active: { buildId: string; builtAt: string; durationMs: number; trigger: string; stats: OwnerGroupStats } | null;
    lastRun: BuildRun | null;
    running: { startedAt: string; trigger: string } | null;
    largest?: { key: string; size: number; isGroup: boolean; people: number }[];
}

export interface DailyPoint {
    day: string;
    calls: number;
    errors: number;
    flagged: number;
    delivered: number;
    returned?: number;
}

export interface FlagCount {
    flag: string;
    count: number;
    lastAt: string;
}

export interface PublicApiOverview {
    build: BuildStatus;
    accounts: { total: number; enabled: number };
    last24h: { calls: number; errors: number; flagged: number; delivered: number; clients: number };
    last7d: { calls: number; delivered: number; clients: number };
    daily: DailyPoint[];
    flags: FlagCount[];
}

export interface ApiUserLabel {
    _id: string;
    email: string;
    name: string | null;
    company: string | null;
}

/** A client's call limiter, set from the BO and never shown to the client. */
export interface RateLimits {
    minIntervalSec: number;
    burst: number;
    cooldownsMin: number[];
    resetAfterMin: number;
    maxPerDay: number;
    maxResults: number;
}

export interface RateNow {
    series: number;
    level: number;
    lockedUntil: string | null;
    lastCallAt: string | null;
    callsToday: number;
    nextCallAllowed: boolean;
    nextCallInSec: number;
    nextCallReason: "cooldown" | "interval" | "daily" | "minute" | null;
}

export interface Throughput {
    callsPerDay: number;
    resultsPerDay: number;
    daysForBase: number | null; // days to receive the whole base at full speed
}

export interface AccountState {
    limits: RateLimits;
    rate: RateNow;
    estimate: Throughput;
    delivered: number;
    base: number;
    coverage: number;
}

export interface PublicApiAccountRow extends AccountState {
    userId: string;
    user: ApiUserLabel | null;
    enabled: boolean;
    activeKeys: number;
    calls24h: number;
    calls7d: number;
    delivered7d: number;
    errors7d: number;
    flagged7d: number;
    lastCallAt: string | null;
    enabledAt?: string;
    notes: string;
}

export interface CallPoint {
    _id: string;
    at: string;
    endpoint: string;
    status: number;
    results: number;
    establishments: number;
    newEstablishments: number;
    retryAfterSec?: number;
    limitReason?: RateNow["nextCallReason"];
    rate?: { series: number; level: number; dayCount: number; lockedUntil?: string };
    deliveredAfter?: number;
    baseSize?: number;
    flags: string[];
    errorCode?: string;
}

export interface PublicApiAccountDetail {
    user: ApiUserLabel;
    account:
        | (AccountState & {
              enabled: boolean;
              notes: string;
              enabledAt?: string;
              enabledBy?: string;
              disabledAt?: string;
              disabledBy?: string;
              requestInProgress: boolean;
          })
        | null;
    keys: { _id: string; name: string; prefix: string; createdAt: string; lastUsedAt?: string; lastUsedIp?: string; usageCount: number; revokedAt?: string; revokedBy?: string; revokeReason?: string }[];
    days: number;
    daily: DailyPoint[];
    calls: CallPoint[];
    endpoints: { endpoint: string; calls: number; errors: number; delivered: number; lastAt: string }[];
    errors: { code: string; count: number; lastAt: string }[];
    ips: { ip?: string; calls: number; firstAt: string; lastAt: string }[];
    userAgents: { userAgent?: string; calls: number; lastAt: string }[];
    flags: FlagCount[];
}

export interface ApiLogRow {
    _id: string;
    requestId: string;
    userId?: string;
    userEmail?: string;
    apiKeyId?: string;
    apiKeyPrefix?: string;
    method: string;
    endpoint: string;
    url: string;
    query?: Record<string, string>;
    body?: Record<string, any>;
    ip?: string;
    forwardedFor?: string;
    userAgent?: string;
    status: number;
    errorCode?: string;
    durationMs: number;
    results: number;
    establishments: number;
    newEstablishments: number;
    ownerGroupIds?: string[];
    retryAfterSec?: number;
    limitReason?: RateNow["nextCallReason"];
    rate?: { series: number; level: number; dayCount: number; lockedUntil?: string };
    deliveredAfter?: number;
    baseSize?: number;
    cursorIn?: string;
    cursorOut?: string;
    scanned?: number;
    flags: string[];
    createdAt: string;
}

export interface ApiLogDetail {
    log: ApiLogRow;
    user: ApiUserLabel | null;
    establishments: { id: string; name: string | null; siret: string | null; siren: string | null; city: string | null; postcode: string | null; franchise: string | null; owners: number; new: boolean }[];
}

export interface LogFilters {
    userId?: string;
    endpoint?: string;
    status?: "ok" | "error";
    flag?: string;
    flagged?: boolean;
    ip?: string;
    before?: string;
    limit?: number;
}

export const getPublicApiOverview = async () => (await axios.get("/admin/public-api/overview", config())).data as PublicApiOverview;

export const getPublicApiAccounts = async (q?: string) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    return (await axios.get(`/admin/public-api/accounts?${params}`, config())).data as { base: number; accounts: PublicApiAccountRow[] };
};

export const getPublicApiAccount = async (userId: string, days = 30) =>
    (await axios.get(`/admin/public-api/accounts/${userId}?days=${days}`, config())).data as PublicApiAccountDetail;

export const updatePublicApiAccount = async (userId: string, body: Partial<{ enabled: boolean; limits: RateLimits; notes: string }>): Promise<{ error?: string }> => {
    try {
        await axios.put(`/admin/public-api/accounts/${userId}`, body, config());
        return {};
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

/** Clears the client's pause, series and today's count: their next call is served. */
export const unlockPublicApiAccount = async (userId: string): Promise<{ error?: string }> => {
    try {
        await axios.post(`/admin/public-api/accounts/${userId}/unlock`, {}, config());
        return {};
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

/** What a set of limits allows at most, before saving it. */
export const estimateLimits = async (limits: RateLimits): Promise<(Throughput & { base: number; maxPageSize: number }) | { error: string }> => {
    try {
        return (await axios.post("/admin/public-api/limits/estimate", { limits }, config())).data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const getPublicApiDeliveries = async (userId: string, before?: string) => {
    const params = new URLSearchParams({ limit: "50" });
    if (before) params.set("before", before);
    return (await axios.get(`/admin/public-api/accounts/${userId}/deliveries?${params}`, config())).data as {
        total: number;
        deliveries: { establishmentId: string; firstAt: string; endpoint: string; requestId: string; name: string | null; siret: string | null; city: string | null; postcode: string | null; active: boolean }[];
        nextBefore: string | null;
    };
};

export const getPublicApiLogs = async (f: LogFilters) => {
    const params = new URLSearchParams();
    if (f.userId) params.set("userId", f.userId);
    if (f.endpoint) params.set("endpoint", f.endpoint);
    if (f.status) params.set("status", f.status);
    if (f.flag) params.set("flag", f.flag);
    else if (f.flagged) params.set("flagged", "1");
    if (f.ip) params.set("ip", f.ip);
    if (f.before) params.set("before", f.before);
    params.set("limit", String(f.limit || 50));
    return (await axios.get(`/admin/public-api/logs?${params}`, config())).data as { logs: ApiLogRow[]; nextBefore: string | null };
};

export const getPublicApiLog = async (id: string) => (await axios.get(`/admin/public-api/logs/${id}`, config())).data as ApiLogDetail;

export const getOwnerGroupStatus = async () => (await axios.get("/admin/public-api/owner-groups/status", config())).data as BuildStatus;

export const rebuildOwnerGroups = async (): Promise<{ error?: string }> => {
    try {
        await axios.post("/admin/public-api/owner-groups/rebuild", {}, config());
        return {};
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

/** Everyone who created a key, with every key and their API access — the "Donner l'accès" list. */
export interface KeyHolder {
    userId: string;
    user: ApiUserLabel;
    access: "enabled" | "disabled"; // on by default; "disabled" only when an admin turned it off
    enabledAt: string | null;
    disabledAt: string | null;
    activeKeys: number;
    lastKeyAt: string;
    lastUsedAt: string | null;
    keys: { _id: string; name: string; prefix: string; createdAt: string; lastUsedAt: string | null; lastUsedIp: string | null; usageCount: number; revokedAt: string | null; revokedBy: string | null }[];
}

export const getKeyHolders = async () => (await axios.get("/admin/public-api/key-holders", config())).data as { holders: KeyHolder[] };

export const searchApiUsers = async (q: string) =>
    (await axios.get(`/admin/public-api/users/search?q=${encodeURIComponent(q)}`, config())).data as { users: (ApiUserLabel & { hasAccount: boolean; enabled: boolean; activeKeys: number })[] };

/** What each flag means, for the BO. */
export const FLAG_LABELS: Record<string, string> = {
    invalid_key: "Clé invalide",
    revoked_key: "Clé désactivée utilisée",
    key_in_query: "Clé passée dans l'URL",
    account_disabled: "Accès API désactivé",
    unknown_param: "Paramètre inconnu",
    bad_param: "Paramètre invalide",
    injection_attempt: "Tentative d'injection",
    cursor_tampered: "Curseur modifié / réutilisé",
    concurrent: "Appels en parallèle",
    rate_limited: "Limite d'appels atteinte (429)",
    early_retry: "A rappelé avant le Retry-After",
    new_ip: "Nouvelle adresse IP",
    report_not_delivered: "Signalement d'un établissement non reçu",
    report_limit: "Trop de signalements",
    server_error: "Erreur serveur",
};

/** Flags that suggest someone is probing the API, not just a clumsy integration. */
export const SERIOUS_FLAGS = new Set(["invalid_key", "revoked_key", "injection_attempt", "cursor_tampered", "concurrent", "report_not_delivered"]);
